import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ContractStep, DetailTotalData } from "src/api/contract/contract-types";
import { getContractCreditAsync, getContractCreditBucketsAsync, updateContractCreditAsync } from "src/api/credit/credit-api";
import { ContractCredit, ContractCreditBucket, ContractCreditUpdateBody } from "src/api/credit/credit-types";
import { useApiOperation } from "src/api/hooks";
import { BaseRadio, BaseTooltip } from "src/components";
import { BaseInputNumberFormat } from "src/components/BaseInputNumberFormat";
import { ArticleSection } from "src/components/layout/ArticleSection";
import { useToast } from "src/recoil/toast/hook";
import { deleteCommas, inputPriceFormat, numberToStringWithComma } from "src/utils";
import CreditBucketTable from "./CreditBucketTable";
import CreditChangeHistoryModal from "./CreditChangeHistoryModal";
import CreditChargeModal from "./CreditChargeModal";

/**
 * 월별 크레딧(버킷)이 존재하는 계약 상태 ('이용중' 전환 시 일괄 지급되어 생성)
 * 이용중 이전 상태에서는 버킷이 없으므로 RCL21(credit-buckets) 조회를 하지 않는다.
 * '이용중' 이후 단계에서는 월별 크레딧이 일괄 지급되어 '월별 기본 지급' 설정 변경이 불가하다.
 */
const CREDIT_BUCKET_STEPS: string[] = [
  ContractStep.USE_PROGRESS,
  ContractStep.USE_COMPLETE,
  ContractStep.TERMINATE_RECEIVED,
  ContractStep.TERMINATE_COMPLETE,
];

type CreditFormData = {
  monthlyCredit: string; // 월별 기본 지급 충전금
  isOverReservationAllowed: boolean; // 초과 예약 허용 여부
  isPostpaidLimited: boolean; // 후불 정산 금액 제한 여부
  postpaidLimitAmount: string; // 후불 정산 금액 한도
  isCreditCarryAllowed: boolean; // 잔여 충전금 이월 허용 여부
};

interface IProps {
  detailData: DetailTotalData;
}

/**
 * 계약 상세 > 공용공간 > 충전금 관리 섹션 (RCL02 조회 / RCL01 수정)
 * - 인라인 편집(ArticleSection): 읽기 ↔ 편집 토글, '해지완료'를 제외한 모든 단계에서 편집 가능
 * - '이용중' 이후에는 편집 모드에서도 '월별 기본 지급'은 값(읽기 전용)으로만 표시
 * - '공간임대(FULL_COURT)' 유형 계약에만 노출
 */
const ContractCreditManagement = ({ detailData }: IProps) => {
  const { openToast } = useToast();
  const contractId = detailData.contract?.contractId ? String(detailData.contract.contractId) : "";
  const contractStep = String(detailData.contract?.contractStep ?? "");
  const spaceProductType = String(detailData.contractManage?.spaceProductType ?? "");

  const isVisible = spaceProductType === "FULL_COURT"; // 공간임대 계약에만 노출
  const isEditAllowed = contractStep !== ContractStep.TERMINATE_COMPLETE; // '해지완료'에서만 수정 버튼 미노출
  const hasBuckets = CREDIT_BUCKET_STEPS.includes(contractStep); // '이용중' 이상에서만 월별 충전금 내역 존재
  const isMonthlyCreditLocked = hasBuckets; // '이용중' 이후에는 '월별 기본 지급' 설정 변경 불가 (값으로만 표시)

  const [isEditable, setIsEditable] = useState(false);
  const [editConfirm, setEditConfirm] = useState(false);
  const [credit, setCredit] = useState<ContractCredit>();
  const [buckets, setBuckets] = useState<ContractCreditBucket[]>([]);
  const [chargeBucket, setChargeBucket] = useState<ContractCreditBucket>(); // 지급/회수 모달 대상
  const [historyBucket, setHistoryBucket] = useState<ContractCreditBucket>(); // 변경내역 모달 대상

  const { executeAsync: getContractCredit } = useApiOperation(getContractCreditAsync);
  const { executeAsync: updateContractCredit } = useApiOperation(updateContractCreditAsync);
  const { executeAsync: getContractCreditBuckets } = useApiOperation(getContractCreditBucketsAsync);

  const {
    control,
    watch,
    setValue,
    reset,
    getValues,
    formState: { dirtyFields },
  } = useForm<CreditFormData>({
    defaultValues: {
      monthlyCredit: "",
      isOverReservationAllowed: false,
      isPostpaidLimited: false,
      postpaidLimitAmount: "",
      isCreditCarryAllowed: false,
    },
    mode: "onChange",
  });

  const fetchCredit = useCallback(async () => {
    if (!contractId) return;
    const res = await getContractCredit({ contractId });
    setCredit(res?.data?.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  // 월별 충전금 내역(버킷)은 계약 '이용중' 전환 시 일괄 생성됨 (이전엔 빈 배열)
  const fetchBuckets = useCallback(async () => {
    if (!contractId) return;
    const res = await getContractCreditBuckets({ contractId });
    setBuckets(res?.data?.data?.contractCreditBucketList ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  useEffect(() => {
    if (!isVisible) return;
    fetchCredit();
    // 월별 충전금 내역은 '이용중' 이상 상태에서만 조회 (이전 상태에선 버킷 미존재)
    if (hasBuckets) fetchBuckets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, hasBuckets, contractId]);

  // 편집 진입 시 현재 값으로 폼 초기화 / 취소 시 편집 종료
  const handleEditableChange = (editing: boolean) => {
    if (editing) {
      // API가 "100000.00" 형태의 소수점 문자열로 내려주므로 Number()로 정수화하여 채움
      reset({
        monthlyCredit: String(Number(credit?.monthlyCredit) || ""),
        isOverReservationAllowed: !!credit?.isOverReservationAllowed,
        isPostpaidLimited: !!credit?.isPostpaidLimited,
        postpaidLimitAmount: String(Number(credit?.postpaidLimitAmount) || ""),
        isCreditCarryAllowed: !!credit?.isCreditCarryAllowed,
      });
    }
    setIsEditable(editing);
  };

  const onClickSave = async () => {
    const v = getValues();
    // 후불 정산 한도 '제한' 선택 시 금액(>0) 필수
    if (v.isOverReservationAllowed && v.isPostpaidLimited && +deleteCommas(v.postpaidLimitAmount || "0") <= 0) {
      openToast({ content: "후불 정산 한도를 입력해 주세요." });
      return;
    }
    // RCL01은 전달된 항목만 갱신하므로, 편집 중 실제로 변경된 항목만 전송한다.
    // (전체 전송 시 다른 관리자가 그사이 바꾼 값을 화면에 로드된 이전 값으로 덮어쓸 수 있음)
    const body: ContractCreditUpdateBody = {};
    if (dirtyFields.monthlyCredit) body.monthlyCredit = +deleteCommas(v.monthlyCredit || "0");
    if (dirtyFields.isCreditCarryAllowed) body.isCreditCarryAllowed = v.isCreditCarryAllowed;
    // 초과 예약 '비허용'으로 변경 시 후불 정산 한도도 함께 초기화되므로 3개 항목은 묶어서 전송
    if (dirtyFields.isOverReservationAllowed || dirtyFields.isPostpaidLimited || dirtyFields.postpaidLimitAmount) {
      body.isOverReservationAllowed = v.isOverReservationAllowed;
      body.isPostpaidLimited = v.isOverReservationAllowed ? v.isPostpaidLimited : false;
      body.postpaidLimitAmount = v.isOverReservationAllowed && v.isPostpaidLimited ? +deleteCommas(v.postpaidLimitAmount || "0") : 0;
    }

    // 변경된 항목이 없으면 API 호출 없이 편집 종료 (기존과 동일하게 완료 토스트는 노출)
    if (Object.keys(body).length === 0) {
      openToast({ content: "정상적으로 수정 되었습니다." });
      setIsEditable(false);
      return;
    }

    const res = await updateContractCredit({ contractId, body });
    if (res?.data?.data) {
      openToast({ content: "정상적으로 수정 되었습니다." });
      setIsEditable(false);
      fetchCredit();
    }
  };

  if (!isVisible) return null;

  const isOverReservationAllowed = watch("isOverReservationAllowed");
  const isPostpaidLimited = watch("isPostpaidLimited");

  return (
    <>
      <ArticleSection
        title="충전금 관리"
        isEditable={isEditable}
        setIsEditable={isEditAllowed ? handleEditableChange : undefined}
        onClickSave={isEditAllowed ? onClickSave : undefined}
        editConfrim={editConfirm}
        setEditConfrim={setEditConfirm}
        closeConfirmOnClick
        disabledState={[contractStep, spaceProductType]}
        editBtnDisabled={hasBuckets} // '이용중' 이후(USE_PROGRESS~)에는 수정 버튼 비활성화
        noHashLink
      >
        <ul className="base-list pb10">
          <li>유료 공용공간 예약시 이용 요금만큼 차감되는 결제수단입니다.</li>
          <li>매월 1일 정해진 충전금이 지급되며, 지급된 충전금은 당월 1일부터 말일까지 사용할 수 있습니다.</li>
        </ul>

        {isEditable ? (
          <>
            {/* 월별 기본 지급 (편집) - '이용중' 이후에는 변경 불가하므로 값만 표시 */}
            <section className="contents-container__grid contents-container__1200">
              <div className="contents-container__grid-index">
                <p className={isMonthlyCreditLocked ? "" : "required"}>월별 기본 지급</p>
              </div>
              <div className="contents-container__grid-contents">
                {isMonthlyCreditLocked ? (
                  <p>{numberToStringWithComma(Number(credit?.monthlyCredit ?? 0))} 원</p>
                ) : (
                  <>
                    <div className="flex-row flex-center-start no-wrap">
                      <div className="minmax160">
                        <Controller
                          control={control}
                          name="monthlyCredit"
                          render={({ field: { onChange, value, name, ref } }) => (
                            <BaseInputNumberFormat
                              inputRef={ref}
                              type="text"
                              onChange={onChange}
                              value={inputPriceFormat(value)}
                              name={name}
                              placeholder="0"
                            />
                          )}
                        />
                      </div>
                      <span className="ml10">원</span>
                    </div>
                    <ul className="base-list pt10">
                      <li>계약이 '이용중' 상태로 전환되면 설정값을 기준으로 계약 전체 기간의 월별 충전금이 일괄 지급됩니다.</li>
                      <li>이후 충전금은 [지급/회수]를 통해서만 조정할 수 있습니다.</li>
                    </ul>
                  </>
                )}
              </div>
            </section>

            {/* 잔여 충전금 이월 (편집) */}
            <section className="contents-container__grid contents-container__1200">
              <div className="contents-container__grid-index">
                <p className="required">잔여 충전금 이월</p>
              </div>
              <div className="contents-container__grid-contents">
                <Controller
                  control={control}
                  name="isCreditCarryAllowed"
                  render={({ field }) => (
                    <div className="flex-row flex-center-start">
                      <BaseRadio
                        id="contractCreditCarryDisallow"
                        name="contractIsCreditCarryAllowed"
                        label="비허용"
                        checked={field.value === false}
                        onChange={() => setValue("isCreditCarryAllowed", false, { shouldValidate: true, shouldDirty: true })}
                      />
                      <BaseTooltip className="ml2 mr16" contents="당월 말일 24시 기준으로 잔여 충전금을 소멸시킵니다." touchIcon="QUESTION" />
                      <BaseRadio
                        id="contractCreditCarryAllow"
                        name="contractIsCreditCarryAllowed"
                        label="허용"
                        checked={field.value === true}
                        onChange={() => setValue("isCreditCarryAllowed", true, { shouldValidate: true, shouldDirty: true })}
                      />
                      <BaseTooltip
                        className="ml2"
                        contents="당월 말일 24시 기준으로 잔여 충전금이 있을 경우, 익월 1일 00:05 ~ 00:10 사이에 익월 충전금으로 이월 지급합니다."
                        touchIcon="QUESTION"
                      />
                    </div>
                  )}
                />
              </div>
            </section>

            {/* 초과 예약 (편집) */}
            <section className="contents-container__grid contents-container__1200">
              <div className="contents-container__grid-index">
                <p className="required">초과 예약</p>
              </div>
              <div className="contents-container__grid-contents">
                <Controller
                  control={control}
                  name="isOverReservationAllowed"
                  render={({ field }) => (
                    <div className="flex-row flex-center-start pb10">
                      <BaseRadio
                        id="contractOverReservationDisallow"
                        name="contractIsOverReservationAllowed"
                        label="비허용"
                        checked={field.value === false}
                        onChange={() => setValue("isOverReservationAllowed", false, { shouldValidate: true, shouldDirty: true })}
                        className="mr16"
                      />
                      <BaseRadio
                        id="contractOverReservationAllow"
                        name="contractIsOverReservationAllowed"
                        label="허용"
                        checked={field.value === true}
                        onChange={() => setValue("isOverReservationAllowed", true, { shouldValidate: true, shouldDirty: true })}
                      />
                    </div>
                  )}
                />

                {/* 후불 정산 한도 (초과 예약 허용 시에만 활성화) */}
                <div className="gray-box">
                  <div className="flex-center no-wrap">
                    <p className="required minmax140">후불 정산 한도</p>
                    <Controller
                      control={control}
                      name="isPostpaidLimited"
                      render={({ field }) => (
                        <div className="flex-center no-wrap">
                          <BaseRadio
                            id="contractPostpaidUnlimited"
                            name="contractIsPostpaidLimited"
                            label="무제한"
                            checked={field.value === false}
                            disabled={!isOverReservationAllowed}
                            onChange={() => setValue("isPostpaidLimited", false, { shouldValidate: true, shouldDirty: true })}
                            className="mr16"
                          />
                          <BaseRadio
                            id="contractPostpaidLimited"
                            name="contractIsPostpaidLimited"
                            label="제한"
                            checked={field.value === true}
                            disabled={!isOverReservationAllowed}
                            onChange={() => setValue("isPostpaidLimited", true, { shouldValidate: true, shouldDirty: true })}
                            className="mr16"
                          />
                        </div>
                      )}
                    />
                    <div className="minmax160">
                      <Controller
                        control={control}
                        name="postpaidLimitAmount"
                        render={({ field: { onChange, value, name, ref } }) => (
                          <BaseInputNumberFormat
                            inputRef={ref}
                            type="text"
                            onChange={onChange}
                            value={inputPriceFormat(value)}
                            name={name}
                            placeholder="0"
                            readonly={!isOverReservationAllowed || !isPostpaidLimited}
                          />
                        )}
                      />
                    </div>
                    <span className="ml10">원</span>
                  </div>
                </div>

                <ul className="base-list pt10">
                  <li>잔여 충전금이 부족할 때 초과 예약 및 후불 정산 허용 여부를 설정합니다.</li>
                  <li>초과 예약 허용 시 후불 정산 가능한 최대 금액을 설정할 수 있습니다.</li>
                </ul>
              </div>
            </section>
          </>
        ) : (
          <>
            {/* 월별 기본 지급 (읽기) */}
            <section className="contents-container__grid contents-container__1200">
              <div className="contents-container__grid-index">
                <p>월별 기본 지급</p>
              </div>
              <div className="contents-container__grid-contents">
                <p>{numberToStringWithComma(Number(credit?.monthlyCredit ?? 0))} 원</p>
              </div>
            </section>

            {/* 잔여 충전금 이월 (읽기) */}
            <section className="contents-container__grid contents-container__1200">
              <div className="contents-container__grid-index">
                <p>잔여 충전금 이월</p>
              </div>
              <div className="contents-container__grid-contents">
                <p>{credit?.isCreditCarryAllowed ? "허용" : "비허용"}</p>
              </div>
            </section>

            {/* 초과 예약 (읽기) */}
            <section className="contents-container__grid contents-container__1200">
              <div className="contents-container__grid-index">
                <p>초과 예약</p>
              </div>
              <div className="contents-container__grid-contents">
                <p>{credit?.isOverReservationAllowed ? "허용" : "비허용"}</p>
                {credit?.isOverReservationAllowed && (
                  <div className="gray-box mt10">
                    <div className="flex-center no-wrap">
                      <p className="font-weight-semibold text-primary3 minmax145">후불 정산 한도</p>
                      <span className="font14">
                        {credit?.isPostpaidLimited ? `제한 (${numberToStringWithComma(Number(credit?.postpaidLimitAmount ?? 0))} 원)` : "무제한"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
        {/* 월별 충전금 내역 (계약 '이용중' 전환 후 노출) */}
        {buckets.length > 0 && (
          <section className="contents-container__grid contents-container__1200">
            <div className="contents-container__grid-index">
              <p>월별 충전금 내역</p>
            </div>
            <div className="contents-container__grid-contents">
              <ul className="base-list pb10">
                <li>월별 지급된 충전금 외에, 필요 시 [지급/회수]를 통해 충전금을 추가 지급하거나 회수할 수 있습니다.</li>
                <li>사용 충전금(확정) 및 청구 대상 요금(확정)은 정산이 완료된 데이터로, 관리자가 임의로 수정하거나 취소·반환할 수 없습니다.</li>
              </ul>
              <CreditBucketTable
                buckets={buckets}
                onClickCharge={(bucket) => setChargeBucket(bucket)}
                onClickHistory={(bucket) => setHistoryBucket(bucket)}
              />
            </div>
          </section>
        )}
      </ArticleSection>

      {/* 지급/회수 모달 (RCL23) */}
      {chargeBucket && (
        <CreditChargeModal
          contractId={contractId}
          bucket={chargeBucket}
          onClose={() => setChargeBucket(undefined)}
          onSaved={() => {
            setChargeBucket(undefined);
            fetchBuckets();
          }}
        />
      )}

      {/* 변경내역 모달 (RCL22) */}
      {historyBucket && <CreditChangeHistoryModal contractId={contractId} bucket={historyBucket} onClose={() => setHistoryBucket(undefined)} />}
    </>
  );
};

export default ContractCreditManagement;
